import React, { useState } from "react";
import { api, streamSSE } from "@/lib/api";
import Markdown from "@/components/Markdown";
import { MAUDE } from "@/constants/testIds";
import { toast } from "sonner";
import { Search, Sparkles, Loader2, ShieldAlert, AlertTriangle } from "lucide-react";

export default function MaudePanel() {
  const [q, setQ] = useState("");
  const [loading, setLoading] = useState(false);
  const [rows, setRows] = useState([]);
  const [summary, setSummary] = useState("");
  const [summarizing, setSummarizing] = useState(false);

  const search = async () => {
    if (!q.trim()) return;
    setLoading(true);
    setRows([]);
    setSummary("");
    try {
      const { data } = await api.get("/maude/search", { params: { q, limit: 50 } });
      setRows(data.results || []);
      if (!data.results?.length) toast.info("No adverse events found for that query");
    } catch (e) {
      toast.error("Search failed");
    } finally {
      setLoading(false);
    }
  };

  const summarize = async () => {
    if (!rows.length) return;
    setSummarizing(true);
    setSummary("");
    try {
      let buf = "";
      await streamSSE(
        "/maude/summarize",
        { device_query: q, events: rows },
        (frame) => {
          if (frame.delta) {
            buf += frame.delta;
            setSummary(buf);
          }
          if (frame.error) toast.error(frame.error);
        }
      );
    } catch (e) {
      toast.error("Summarize failed");
    } finally {
      setSummarizing(false);
    }
  };

  const severity = (ev) => {
    const t = (ev.event_type || "").toLowerCase();
    if (t.includes("death")) return { c: "text-red-400 border-red-400/40", l: "DEATH" };
    if (t.includes("injury")) return { c: "text-amber-400 border-amber-400/40", l: "INJURY" };
    if (t.includes("malfunction")) return { c: "text-blue-300 border-blue-300/40", l: "MALFUNC" };
    return { c: "text-slate-500 border-slate-200", l: (ev.event_type || "OTHER").toUpperCase() };
  };

  return (
    <div>
      <div className="mb-6">
        <div className="overline mb-2 text-slate-500">openFDA · MAUDE live</div>
        <h1 className="font-serif text-3xl font-semibold tracking-tight lg:text-4xl">
          Adverse-event intelligence.
        </h1>
        <p className="mt-2 max-w-2xl text-sm text-slate-500">
          Search MDR reports from FDA's MAUDE database, then get an AI failure-mode
          brief with ISO 14971 risk-management implications.
        </p>
      </div>

      <div className="mb-6 flex flex-wrap gap-3">
        <div className="flex flex-1 items-center border border-slate-200 bg-white pl-4">
          <Search className="h-4 w-4 text-slate-400" />
          <input
            data-testid={MAUDE.searchInput}
            value={q}
            onChange={(e) => setQ(e.target.value)}
            onKeyDown={(e) => e.key === "Enter" && search()}
            placeholder="e.g., insulin pump, hip implant, defibrillator…"
            className="flex-1 border-none bg-transparent px-3 py-3 text-sm text-slate-900 placeholder:text-slate-400 focus:outline-none"
          />
        </div>
        <button
          data-testid={MAUDE.searchBtn}
          onClick={search}
          disabled={loading}
          className="inline-flex items-center gap-2 bg-[#2563EB] px-5 py-3 text-sm font-semibold text-white transition-all hover:bg-[#1D4ED8] disabled:opacity-50"
        >
          {loading ? <Loader2 className="h-4 w-4 animate-spin" /> : <Search className="h-4 w-4" />}
          Search
        </button>
      </div>

      {rows.length > 0 && (
        <>
          <div className="mb-6 flex items-center justify-between border border-slate-200 bg-white px-4 py-3">
            <div className="flex items-center gap-3">
              <ShieldAlert className="h-4 w-4 text-[#2563EB]" />
              <div className="overline text-slate-500">{rows.length} MDR REPORT{rows.length === 1 ? "" : "S"}</div>
            </div>
            <button
              data-testid={MAUDE.summarizeBtn}
              onClick={summarize}
              disabled={summarizing}
              className="inline-flex items-center gap-2 bg-white px-4 py-2 text-xs font-semibold tracking-wide text-black transition-all hover:bg-[#2563EB] hover:text-white disabled:opacity-50"
            >
              {summarizing ? <Loader2 className="h-3.5 w-3.5 animate-spin" /> : <Sparkles className="h-3.5 w-3.5" />}
              AI Failure-Mode Brief
            </button>
          </div>

          <div className="mb-8 grid gap-3">
            {rows.map((ev, i) => {
              const sev = severity(ev);
              return (
                <div
                  key={ev.report_number || i}
                  data-testid={MAUDE.resultRow}
                  className="border border-slate-200 bg-white p-4 transition-colors hover:border-slate-300"
                >
                  <div className="mb-2 flex flex-wrap items-center gap-3">
                    <span className={`border px-2 py-0.5 font-mono text-[10px] tracking-wider ${sev.c}`}>
                      {sev.l}
                    </span>
                    <span className="font-mono text-xs text-slate-400">#{ev.report_number}</span>
                    {ev.date_received && (
                      <span className="overline text-slate-400">RECEIVED {ev.date_received}</span>
                    )}
                  </div>
                  <div className="mb-1 font-medium text-slate-900">
                    {ev.brand_name || ev.generic_name || "Unknown device"}
                  </div>
                  <div className="text-xs text-slate-500">{ev.manufacturer_name || "Manufacturer N/A"}</div>
                  {ev.product_problems?.length > 0 && (
                    <div className="mt-2 flex flex-wrap gap-1.5">
                      {ev.product_problems.slice(0, 4).map((p, j) => (
                        <span key={j} className="border border-slate-200 bg-slate-50 px-2 py-0.5 text-[11px] text-slate-600">
                          {p}
                        </span>
                      ))}
                    </div>
                  )}
                  {ev.narrative && (
                    <p className="mt-3 text-sm leading-relaxed text-slate-500 line-clamp-3">
                      {ev.narrative}
                    </p>
                  )}
                </div>
              );
            })}
          </div>
        </>
      )}

      {(summary || summarizing) && (
        <div className="border border-slate-200 bg-white p-6 lg:p-8">
          <div className="mb-5 flex items-center gap-2">
            <AlertTriangle className="h-4 w-4 text-[#2563EB]" />
            <div className="overline text-[#2563EB]">FAILURE-MODE & RISK BRIEF</div>
          </div>
          {summary ? (
            <Markdown>{summary}</Markdown>
          ) : (
            <div className="flex items-center gap-2 text-slate-500">
              <span className="typing-dot" /><span className="typing-dot" /><span className="typing-dot" />
              <span className="ml-2 text-sm">Analyzing MDR reports…</span>
            </div>
          )}
        </div>
      )}
    </div>
  );
}
